/**
 * Canonical courier-delivery lifecycle — THE single status vocabulary that
 * every CLIENT surface binds to when rendering courier / Dasher status:
 *   - the management web Active Deliveries board,
 *   - the POS deliveries screen (mirrored verbatim as a Dart enum),
 *   - any future customer-facing tracking surface.
 *
 * The backend stamps `order.courierStatus` as the RAW provider string — a
 * DoorDash Drive §7.2 `event_name` (UPPER_SNAKE), a Drive snapshot
 * `delivery_status` (lower_snake), or a marketplace Dasher-status string. This
 * module maps raw → canonical so every surface renders ONE consistent set of
 * badges, buckets, and timeline steps instead of each re-implementing the map
 * (the prior drift: a copy lived in apps/management .../deliveries/courierStatus.ts).
 *
 * Relationship to core's `CourierDeliveryStatus`
 * (packages/core/src/delivery/contracts/types.ts): that 8-value type is the
 * server-side provider-normalization output. `OrderCourierPhase` is the same 8
 * values PLUS `'unknown'` (for raw input that doesn't map) — the display
 * vocabulary. They are deliberately kept structurally compatible.
 *
 * PURE DATA — no React, no shadcn, no Firebase — so it is safe to import from
 * client React components and to mirror byte-for-byte in the Flutter POS.
 */

/**
 * Canonical courier lifecycle — the 8 real phases + `unknown`.
 *
 * `returned` is terminal: the courier could not hand the food over and brought
 * it BACK to the store (Uber Direct `returned`, Drive's completed return). It is
 * kept apart from `failed` because the food is physically back at the location —
 * an operator decision (re-dispatch, refund, hand to the guest) rather than a
 * lost delivery.
 */
export type OrderCourierPhase =
  | 'created'
  | 'courier_assigned'
  | 'picked_up'
  | 'en_route'
  | 'delivered'
  | 'cancelled'
  | 'failed'
  | 'returned'
  | 'unknown';

/** All phase values, in lifecycle order (terminal-divergent phases last). */
export const ORDER_COURIER_PHASES: readonly OrderCourierPhase[] = [
  'created',
  'courier_assigned',
  'picked_up',
  'en_route',
  'delivered',
  'cancelled',
  'failed',
  'returned',
  'unknown',
] as const;

/**
 * RAW provider courier-status string → canonical phase.
 *
 * Keys cover (lookup is case- and whitespace-insensitive, see
 * `mapRawCourierStatusToPhase`, so one lower-cased entry serves both
 * `CREATED` and `created`):
 *   - DoorDash Drive §7.2 `event_name` values (DASHER_CONFIRMED, …)
 *   - DoorDash Drive snapshot `delivery_status` values (enroute_to_pickup, …)
 *   - common marketplace Dasher-status strings (accepted, assigned, …)
 *   - Uber Direct delivery `status` values (pending, pickup, pickup_complete,
 *     dropoff, delivered, canceled, returned)
 *   - every canonical phase itself (identity), so a value a writer already
 *     normalised reads back unchanged
 *
 * Mirrors core's DRIVE_STATUS_MAP / DRIVE_EVENT_STATUS_MAP
 * (packages/core .../delivery/shared/courierMilestones.ts). Keep the two in
 * sync when DoorDash adds event names — this is the display superset.
 */
const RAW_COURIER_STATUS_MAP: Readonly<Record<string, OrderCourierPhase>> = {
  // ── Pre-assignment / requested ──────────────────────────────────
  created: 'created',
  quote: 'created',
  quote_accepted: 'created',
  scheduled: 'created',
  delivery_batched: 'created',

  // ── Courier assigned / confirmed / enroute-to-pickup ────────────
  courier_assigned: 'courier_assigned',
  dasher_confirmed: 'courier_assigned',
  dasher_confirmed_pickup_arrival: 'courier_assigned',
  dasher_enroute_to_pickup: 'courier_assigned',
  enroute_to_pickup: 'courier_assigned',
  confirmed: 'courier_assigned',
  accepted: 'courier_assigned',
  assigned: 'courier_assigned',
  arrived_at_pickup: 'courier_assigned',

  // ── Picked up ───────────────────────────────────────────────────
  picked_up: 'picked_up',
  dasher_picked_up: 'picked_up',
  pickup_complete: 'picked_up',

  // ── En route to dropoff ─────────────────────────────────────────
  en_route: 'en_route',
  enroute: 'en_route',
  enroute_to_dropoff: 'en_route',
  dasher_enroute_to_dropoff: 'en_route',
  dasher_confirmed_dropoff_arrival: 'en_route',
  arrived_at_dropoff: 'en_route',

  // ── Delivered (terminal, success) ───────────────────────────────
  delivered: 'delivered',
  dasher_dropped_off: 'delivered',
  dropped_off: 'delivered',
  completed: 'delivered',

  // ── Cancelled (terminal) ────────────────────────────────────────
  cancelled: 'cancelled',
  canceled: 'cancelled',
  delivery_cancelled: 'cancelled',

  // ── Uber Direct (pending / pickup / dropoff) ────────────────────
  // `pickup` is "courier on the way to the store", `dropoff` "on the way to
  // the guest"; the rest (`pickup_complete`, `delivered`, `canceled`,
  // `returned`) already appear in their buckets above/below.
  pending: 'created',
  pickup: 'courier_assigned',
  dropoff: 'en_route',

  // ── Failed (terminal) — includes a return still IN PROGRESS ─────
  failed: 'failed',
  delivery_attempt_failed: 'failed',
  delivery_return_initialized: 'failed',
  dasher_confirmed_return_arrival: 'failed',
  dasher_enroute_to_return: 'failed',

  // ── Returned (terminal) — the food is back at the store ─────────
  returned: 'returned',
  delivery_returned: 'returned',
};

/**
 * Map a RAW provider courier-status string to the canonical phase.
 * Case- and whitespace-insensitive. Unknown / missing inputs → `'unknown'`.
 */
export function mapRawCourierStatusToPhase(
  raw?: string | null,
): OrderCourierPhase {
  if (!raw) return 'unknown';
  const key = raw.trim().toLowerCase();
  if (!key) return 'unknown';
  return RAW_COURIER_STATUS_MAP[key] ?? 'unknown';
}

/** Phases for a courier that is no longer in flight (success OR failure). */
export const TERMINAL_COURIER_PHASES: ReadonlySet<OrderCourierPhase> = new Set([
  'delivered',
  'cancelled',
  'failed',
  'returned',
]);

/** Phases that warrant an operator-facing red alert. */
export const ALERT_COURIER_PHASES: ReadonlySet<OrderCourierPhase> = new Set([
  'cancelled',
  'failed',
  'returned',
]);

/** Phases meaning the courier is actively moving the food to the customer. */
export const OUT_FOR_DELIVERY_PHASES: ReadonlySet<OrderCourierPhase> = new Set([
  'picked_up',
  'en_route',
]);

/** `true` when the phase represents a courier no longer in flight. */
export function isTerminalCourierPhase(phase: OrderCourierPhase): boolean {
  return TERMINAL_COURIER_PHASES.has(phase);
}

/** Human-readable label for a canonical courier phase. */
export function courierPhaseLabel(phase: OrderCourierPhase): string {
  switch (phase) {
    case 'created':
      return 'Courier Requested';
    case 'courier_assigned':
      return 'Courier Assigned';
    case 'picked_up':
      return 'Picked Up';
    case 'en_route':
      return 'En Route';
    case 'delivered':
      return 'Delivered';
    case 'cancelled':
      return 'Courier Cancelled';
    case 'failed':
      return 'Delivery Failed';
    case 'returned':
      return 'Returned to Store';
    case 'unknown':
    default:
      return 'Courier Status Unknown';
  }
}

/**
 * The ordered happy-path steps for a courier-progress timeline/stepper
 * (Created → Assigned → Picked Up → En Route → Delivered). Terminal-divergent
 * phases (`cancelled`/`failed`/`returned`) and `unknown` are NOT steps — they are rendered
 * as a distinct terminal state by the UI, not a point on this track.
 */
export const COURIER_PHASE_STEPS: readonly OrderCourierPhase[] = [
  'created',
  'courier_assigned',
  'picked_up',
  'en_route',
  'delivered',
] as const;

/**
 * Index of `phase` within `COURIER_PHASE_STEPS`, used to render how far along a
 * courier-progress stepper to fill. Returns `-1` for `cancelled`/`failed`/
 * `returned`/`unknown` (off-track) so the UI can branch to a terminal-divergent rendering.
 */
export function courierPhaseStepIndex(phase: OrderCourierPhase): number {
  return COURIER_PHASE_STEPS.indexOf(phase);
}
